import { useMemo } from "react";
import { useSelector } from "react-redux";

export const groupBy = (array, key) => {
    return array.reduce((result, currentValue) => {
        (result[currentValue[key]] = result[currentValue[key]] || []).push(currentValue);
        return result;
    }, {});
};
const getItemExtras = (item) => {
  const groupedExtras = groupBy(item.subProducts, 'isIncluded');

  let currentFreeExtraIdx = 0;
  const includedExtras = [...(groupedExtras[true] ?? [])].map(el => ({ name: el.name, cost: el.cost * el.quantity, quantity: el.quantity, isIncluded: el.isIncluded }));

  const excludedExtras = groupedExtras[false]?.sort((a, b) => b.cost - a.cost).reduce((acc, cur) => {
    const freeExtrasAvailable = groupedExtras[true]?.length ?? 0;
    const freeExtrasToUse = Math.min(cur.quantity, freeExtrasAvailable - currentFreeExtraIdx);
    const newItem = {
      name: cur.name,
      cost: (cur.quantity - freeExtrasToUse) * cur.cost,
      quantity: cur.quantity,
      isIncluded: cur.isIncluded,
      id: cur.id
    };

    currentFreeExtraIdx += freeExtrasToUse;
    if (!cur.isGroup && cur.id != "1") acc.push(newItem);
    return [...acc, ...getItemExtras(cur)];
  }, []);
  return includedExtras.concat(excludedExtras ?? []);
}

export const getDuoRomanaExtras = (item) => {
  const extras = item.subProducts?.reduce((acc, cur) => {
    if (!cur.isGroup && cur.id != 1 && !cur.isIncluded) { // If we are on the base/pizza half
      const toppings = getItemExtras(cur);
      return [
        ...acc,
        {
          base: cur.name,
          includedToppings: toppings.filter(el => el.isIncluded),
          excludedToppings: toppings.filter(el => !el.isIncluded)
        }
      ];
    }

    return [...acc, ...getDuoRomanaExtras(cur)];
  }, []);
  return extras ?? [];
}

const getBundleOptions = (bundle) => {
  const optionsArr = bundle.sections.reduce((acc, cur) => {
    const products = cur.products.map(p => ({
      name: p.product.subProducts?.[0]?.subProducts?.[0].name ? `${p.product.name} ${p.product.subProducts?.[0]?.subProducts?.[0].name}` : p.product.name,
      cost: p.product.upliftPrice ? p.product.upliftPrice : 0,
      quantity: p.product.quantity
    }));
    return acc.concat(products);
  }, []);
  return optionsArr;
};

// Extracts a clean display name from a product name by:
// 1. Splitting on hyphen, but preserving "gluten-" (e.g. "Margherita - Large" → "Margherita" but "Gluten-Free" stays intact)
// 2. Stripping dietary tags like [V], [ve], [GF] or (GF)
const getCleanDisplayName = (name) =>
  name
    ?.split(/(?<!gluten)-/i)[0]
    ?.replace(/\s*(\[.*?\]|\(GF\))/gi, '')
    .trim();

export const getExtras = (item, isBundle, isPiccolo, doughStickOptionIds) => {
  if (item.customID && !isBundle) {
    const newExtras = getItemExtras(item);
    const hasDoughStickOption = newExtras.some(ne => doughStickOptionIds?.includes(ne.id));
    if (isPiccolo || hasDoughStickOption) {
      return { base: '', includedToppings: newExtras.filter(el => el.isIncluded), excludedToppings: newExtras.filter(el => !el.isIncluded) };
    }
    else {
      return { base: getCleanDisplayName(newExtras[0]?.name), includedToppings: newExtras.slice(1).filter(el => el.isIncluded), excludedToppings: newExtras.slice(1).filter(el => !el.isIncluded) };
    }
  } else if (isBundle) {
    const newExtras = getBundleOptions(item);
    return { items: newExtras };
  }
};

export const copyMenuItemWithoutSubItems = ({ subProducts, ...item }) => item;

export const addParentReferences = (item, parent) => {
  if (!item) return null;
  const newItem = { ...item };
  if (parent) {
    const parentx2 = { ...parent.parent, parent: null };
    newItem.parent = { ...parent, parent: parentx2 };
  }
  newItem.subProducts = item?.subProducts?.map(el => addParentReferences(el, newItem));
  return newItem;
};

export const copyMenuItemWithoutParent = (item) => {
  const result = { ...item };
  delete result.parent;
  return result;
};

export function calcSubTotal(parentCost = 0) {
  // Ensure 'this' context remains intact for function calls
  if (!this.subProducts) return parentCost;

  const groupedSubproducts = groupBy(this.subProducts, 'isIncluded');
  const included = groupedSubproducts[true] ?? [];
  const excluded = groupedSubproducts[false] ?? [];

  const freeExtrasAvailable = included.length;
  let freeExtrasUsed = 0;

  const childrenTotal = excluded
    .sort((a, b) => b.cost - a.cost) // Sort by cost descending
    .reduce((total, topping) => {
      const subTotal = calcSubTotal.call(topping, topping.cost);
      if (!subTotal || subTotal <= 0) return total;

      const quantity = Math.max(topping.quantity || 1, 1);
      const remainingToppingsToAdd = Math.max(
        quantity - Math.max(freeExtrasAvailable - freeExtrasUsed, 0),
        0
      );

      freeExtrasUsed += quantity;
      return total + subTotal * remainingToppingsToAdd;
    }, 0);

  return parentCost + childrenTotal;
}

export const convertMenuItemToBasketItem = ({ parent, min, max, subProducts, ...item }) => {
  return { ...item, quantity: 1, subProducts: [], calcSubTotal };
};

export const findQuestionOptions = (customItem, questionID, foundSelectHalfQuestionId = false, selectHalfQuestionId) => {
  if (!foundSelectHalfQuestionId) {
    foundSelectHalfQuestionId = customItem.id === selectHalfQuestionId;
  }
  if (customItem?.subProducts?.length) {
    const question = customItem.subProducts.find(el => el.name === questionID);
    if (question && question.isGroup) {
      return foundSelectHalfQuestionId ? question.subProducts : [];
    } else if (customItem.subProducts.length) {
      return customItem.subProducts.reduce((acc, cur) => [...acc, ...findQuestionOptions(cur, questionID, foundSelectHalfQuestionId, selectHalfQuestionId)], []);
    }
  } else {
    return [];
  }
};

export const findAllCustomOptions = (customItem) => {
  let result = [];

  if (customItem?.subProducts?.length) {
    for (const subProduct of customItem.subProducts) {
      if (subProduct.isGroup) {
        // Collect subProducts directly if isGroup is true
        result = result.concat(subProduct.subProducts);
      }
      // Recurse into each subProduct to find more matches
      result = result.concat(findAllCustomOptions(subProduct));
    }
  }

  return result;
};

const findAndMatchOptions = (existingItems, newItems) => {
  if (!existingItems?.length || !newItems?.length) return [];
  return newItems.reduce((acc, newItem) => {
    const exists = existingItems.find(sp => sp.name.toLowerCase() === newItem.name.toLowerCase() && sp.isIncluded === newItem.isIncluded);
    if (exists) {
      const matchedOptions = findAndMatchOptions(exists.subProducts, newItem.subProducts);
      acc.push({ ...newItem, quantity: exists.quantity, subProducts: matchedOptions });
    }
    return acc;
  }, []);
}

export const addMenuItemToQuestion = (item, question, option) => {
  const radio = item.min === 1 && item.max <= item.min;
  if (radio) {
    const existingChildQuestionsWithAppliedOptions = question.subProducts?.[0]?.subProducts;
    const incomingQuestions = [...option.subProducts];
    const persistableQuestions = findAndMatchOptions(existingChildQuestionsWithAppliedOptions, incomingQuestions);
    while (question.subProducts.length) { question.subProducts.pop(); }
    const formattedOption = convertMenuItemToBasketItem(option);
    if (persistableQuestions?.length) {
      formattedOption.subProducts = persistableQuestions;
      question.subProducts.push(formattedOption);
    }
    else
      question.subProducts.push(applyDefaultSubProducts(option)); // prevent
  } else {
    question.subProducts.push(convertMenuItemToBasketItem(option));
  }
};


export const addToppingToQuestion = (item, question, option) => {
  const currentTotal = question.subProducts.reduce((a, b) => a + b.quantity, 0);
  if (currentTotal < item.max) {
    let orderTopping = question.subProducts.find(p => p.id === option.id);
    if (!orderTopping) {
      orderTopping = convertMenuItemToBasketItem(option);
      question.subProducts.push(orderTopping);
    } else {
      orderTopping.quantity++;
    }
  }
};

export const removeToppingToQuestion = (item, question, option) => {
  let orderTopping = question.subProducts.find(p => p.id === option.id);
  if (orderTopping) {
    orderTopping.quantity--;
    if (orderTopping.quantity <= 0) {
      question.subProducts = question.subProducts.filter(p => p.id !== option.id);
    }
  }
};

export const toggleIncludedItem = (item, question, option) => {
  const currentTotal = question.subProducts.reduce((a, b) => a + b.quantity, 0);
  if (currentTotal < item.max) {
    let orderTopping = question.subProducts.find(p => p.id === option.id);
    if (!orderTopping) {
      orderTopping = convertMenuItemToBasketItem(option);
      question.subProducts.push({ ...orderTopping, quantity: -1 });
    } else {
      question.subProducts = question.subProducts.filter(sp => sp.id !== option.id);
    }
  }
};

export const resetQuestion = (item, parentQuestion, option) => {
  const question = parentQuestion.subProducts.find(el => option.id === el.id);
  if (question) {
    question.subProducts = [];
  } else {
    parentQuestion.subProducts.forEach(el => resetQuestion(null, el, option));
  }
};

export const dereffItems = (item) => {
  return { ...item, subProducts: item.subProducts.map(dereffItems), calcSubTotal };
};
export const structureQuickAddItem = (item) => {
  const itemWithParents = addParentReferences(item);
  const defaultItem = applyDefaultSubProducts(itemWithParents);
  return dereffItems(defaultItem);
};

export const applyDefaultSubProducts = (item, isDuoRomana = false) => {
  if (!item || !Array.isArray(item.subProducts) || item.subProducts.length === 0) {
    return { ...item, meta: {}, quantity: 1, isPlaceholder: true };
  }

  const getDefaultSubProduct = (item) => {
    const defaultItem = item.subProducts.find(el => el.id === item.defaultSubProduct);
    return defaultItem || item.subProducts[0];
  };

  let newItem = { ...item, meta: {}, isPlaceholder: true };

  if (item.defaultSubProduct) {
    const defaultItem = getDefaultSubProduct(item);
    const defaultItemWithDefaults = applyDefaultSubProducts(defaultItem, isDuoRomana);
    newItem = { ...item, subProducts: [defaultItemWithDefaults], isPlaceholder: true };
  } else if ((!item.defaultSubProduct && item.max > 1) || item.isGroup) {
    if (isDuoRomana) {
      const hasSubProducts = item.subProducts[0].subProducts.length !== 0;
      const updatedSubProducts = hasSubProducts
        ? [applyDefaultSubProducts(item.subProducts[0], isDuoRomana)]
        : [];

      newItem = { ...item, subProducts: updatedSubProducts, isPlaceholder: true };

    } else {
      newItem.subProducts = [];
    }
  } else {
    newItem.subProducts = item.subProducts.map(el => applyDefaultSubProducts(el, isDuoRomana));
  }

  return newItem;
};

export const getCals = (item, selectedBase, getDefault = false) => {
  if (!item) return '';

  const { calories, subProducts } = item;
  const baseChoices = subProducts?.[0]?.subProducts;

  if (baseChoices?.length) {
    if (selectedBase) {
      const selectedBaseCalories = baseChoices.find(base => base.id === selectedBase)?.calories;
      if (selectedBaseCalories) return selectedBaseCalories;
    }

    if (getDefault) {
      const defaultBaseId = subProducts[0]?.defaultSubProduct;
      const classicBaseCalories = baseChoices.find(base => base.id === '26')?.calories;
      const defaultBaseCalories = baseChoices.find(base => base.id === defaultBaseId)?.calories;
      if (classicBaseCalories) return classicBaseCalories;
      else if (defaultBaseCalories) return defaultBaseCalories;
    }

    if (baseChoices[0]?.calories) return baseChoices[0]?.calories;
  }

  return calories ?? '';
};

export const getUniqueItems = (items) => {
  var unique = [];
  var distinct = [];

  for (let i = 0; i < items.length; i++) {
    if (!unique[items[i].id]) {
      distinct.push(items[i]);
      unique[items[i].id] = 1;
    }
  }

  return distinct;
};

// Check if any subproduct array has more than 1 option
export const checkIfCustomisable = (item) => (item.subProducts && item.subProducts.length > 1);

// Check if any subproduct has a min of more than 0, a subProduct array of more than 1 that does't include the defaultSubProduct id
const checkIfAddable = (item) => !(item.min > 0 && item.subProducts.length > 1 && !item.subProducts.find(subProduct => subProduct.id === item.defaultSubProduct));

export const checkIfPopUpRequired = (item, popupModalIds) => {
  if (
    item.min > 0 &&
    item.subProducts.length > 1 &&
    (popupModalIds?.includes(item.id))
  ) {
    return item;
  }
};

export const getQuickAddProperties = (item, popupModalIds) => {

  let result = {
    isAddable: true,
    isCustomisable: false,
    isPizza: false,
    popupRequired: false,
    shouldHoistTags: false
  };

  if (!item) return result;
  result.shouldHoistTags = item.subProducts?.[0]?.subProducts?.length === 1 ?? false;
  result.isPizza = item.subProducts?.[0]?.name === 'Choose your base' && item.subProducts?.[0]?.subProducts?.length > 1;

  const recursiveCheck = (item, i, popupModalIds) => {
    if (item.subProducts) {
      if (popupModalIds) {
        const popupChoice = checkIfPopUpRequired(item, popupModalIds);
        if (popupChoice) result.popupRequired = popupChoice;
      }
      if (!checkIfAddable(item)) result.isAddable = false;
      if (checkIfCustomisable(item)) result.isCustomisable = true;
      item.subProducts.forEach(subProduct => recursiveCheck(subProduct, i + 1, popupModalIds));
    }
  };
  item.subProducts?.forEach(subProduct => recursiveCheck(subProduct, 1, popupModalIds));
  return result;
};

export const useQuickAddProperties = (item) => {
  const popupModalIds = useSelector(state => state.session.frontEndConfig?.popupModalIds);
  const quickAddProperties = useMemo(() => getQuickAddProperties(item, popupModalIds), [item]);
  return quickAddProperties;
}

export const makeItAMealPopupRequired = (item, base, makeItAMealBundles, makeItAMealBundleOptions, makeItAMealVariant, makeItAMealModalTimesShown) => {
  if (!(makeItAMealVariant === 'C' || (makeItAMealVariant === 'B' && makeItAMealModalTimesShown < 1))) {
    return false;
  }

  let itemName = item.name;
  if (!base && itemName.toLowerCase().endsWith('- large classic')) {
    base = '111';
    itemName = itemName.replace(/- large classic/i, '').trim();   
  }
  else if (itemName === 'American' && base === '111') { // This is hacky but it's the only way to differ between American and American Hot when the id's are different to that of the bundle (when its from the pizza category with a large classic base instead of in the 13 inch large classic category)
    itemName = `${itemName} - Large Classic`;    
  }

  const bundleName = makeItAMealBundleOptions.find(b => b.baseId === base)?.bundleName;
  const bundlePizzaChoices = makeItAMealBundles[bundleName] ?? [];
  // First - try to match by ID
  let selectedPizza = bundlePizzaChoices?.find(pizza => pizza.id === item.id)?.id;
  if (!selectedPizza) { // Second - try to match by name    
    selectedPizza = bundlePizzaChoices?.find(pizza => pizza.name.startsWith(`${itemName}`))?.id;
  }
  if (selectedPizza) {
    return { bundleName, selectedPizza };
  } else {
    return false;
  }
}

export const setDefaultSubProducts = (item, choices) => {
  const recurseAndSetDefault = (subProducts, choice) => {
    if (!subProducts?.length) return;
    subProducts.forEach(sp => {
      if (sp.id == choice.id) {
        sp.defaultSubProduct = choice.defaultId;
      }
      else recurseAndSetDefault(sp.subProducts, choice);
    });
  }
  choices.forEach(c => recurseAndSetDefault(item.subProducts, c));
};

export const checkIfPizza = (item) => {
  return item?.[0]?.name === 'Choose your base' && item?.[0]?.subProducts?.length > 1;
};

export const getCardButtons = (props, quickAdd, navigateTo, restaurantId, id, handleCustomiseClick, isCustomisable, isAddable) => {
  const buttons = [];
  if (isCustomisable && !props.outOfStock) buttons.push({ text: 'Customise', disabled: false, onClick: () => { handleCustomiseClick(); navigateTo(`/menu/${restaurantId}/customise/${id}`, { menuItem: props.item }) }, testId: 'navigate-customise' });
  if (isCustomisable && props.outOfStock) buttons.push({ text: 'Customise', disabled: true, onClick: () => { }, testId: 'navigate-customise' });
  if (isAddable && !props.outOfStock) buttons.push({ text: 'Add', disabled: false, onClick: quickAdd, testId: 'navigate-add' });
  if (isAddable && props.outOfStock) buttons.push({ text: 'Add', disabled: true, onClick: () => { }, testId: 'navigate-add' });

  return buttons;
};

export const getDefaultPortion = (item) => {
  if (!checkIfCustomisable(item.subProducts) || !item.subProducts[0].defaultSubProduct) return false;
  return item.subProducts[0].subProducts.find(sp => sp.id === item.subProducts[0].defaultSubProduct);
};

export const valueWithAppliedRewards = (value, discounts) => {
  const rewardTotal = discounts.reduce((acc, d) => (acc + d.amount), 0);
  const subTotalAppliedRewards = value - rewardTotal;
  return subTotalAppliedRewards;
};

export const checkCanSaveBasket = (restaurantClosingTime) => {
  if (!restaurantClosingTime) return false;
  const [hours, minutes] = restaurantClosingTime.split(':');
  const closingTime = new Date();
  closingTime.setHours(+hours);
  closingTime.setMinutes(+minutes);
  const cutoffTime = new Date().setTime(closingTime.getTime() - 20 * 60 * 1000);
  const now = new Date().getTime();
  return now < cutoffTime;
}

export const getTags = (item, arrayName, excludeChildTags = true, currentTier) => {
  if (!item || !item?.[arrayName]?.length) return [];
  try {
    let tags = [...item[arrayName]];
    if (excludeChildTags) {
      const childTags = item.subProducts.flatMap(sp => sp.subProducts.flatMap(i => i[arrayName] ?? []) ?? []);
      tags = tags.filter(t => !childTags.includes(t));
    }
    if (currentTier) {
      tags = tags.filter(t => t === currentTier)
    }
    return tags.filter(onlyUnique);
  } catch (err) {
    console.error(item, arrayName)
  }
}

const onlyUnique = (value, index, array) => array.indexOf(value) === index;

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}